import Mailcheck from "mailcheck";
import { cond, always, T } from "ramda";
import { isBefore, isValid, isAfter } from "date-fns";
import { parseSimpleDateStringToDate, isValidDate, yearsAgo } from "./date";

export const testMinLength = (minLength: number) => (value: string) => {
  return minLength > value.trim().length;
};

/**
 * Test if value is an incomplete date
 * @return true if incomplete date
 */
export const testIncompleteDate = (value: string) => {
  return !isValid(parseSimpleDateStringToDate(value));
};

/**
 * Test if value is an invalid date
 * e.g. 32/06/2020 - no 32nd day of the month!
 * or 30/02/2002 - no 30th day in February!
 * @return true if invalid date
 */
export const testInvalidDate = (value: string) => {
  const [day, month, year] = value.split("/").map(Number);
  return !isValidDate(day, month - 1, year);
};

/**
 * Test if value is before specified min date
 * @return true if date is before min date
 */
export const testMinDate = (minDate: Date) => (value: string) => {
  const date = parseSimpleDateStringToDate(value);
  return isBefore(date, minDate);
};

/**
 * Test if value is after specified max date
 * @return true if date is after max date
 */
export const testMaxDate = (maxDate: Date) => (value: string) => {
  const date = parseSimpleDateStringToDate(value);
  return isAfter(date, maxDate);
};

/**
 * Test if value is an invalid NHS number
 * Check out https://www.datadictionary.nhs.uk/data_dictionary/attributes/n/nhs/nhs_number_de.asp
 * if you really want to get into it
 * @return true if NHS number is invalid
 */
export const testInvalidNhsNumber = (value: string) => {
  // Weighing factor is relative to the digits position, starting at
  // 10 and going down to 2
  let baseWeighingFactor = 10;

  // Remove any spaces
  const trimmed = value.replace(/\s+/g, "");

  // The NHS number is 10 numeric digits in length.
  // The tenth digit is a check digit (`checkDigit`) used to confirm its validity when compared
  // to the first 9 digits (named `digits` here).
  const digits = trimmed
    .slice(0, 9)
    .split("")
    .map(char => parseInt(char, 10));
  const checkDigit = parseInt(trimmed.charAt(9), 10);

  // Multiply each of the first nine digits by a weighting factor and sum to accumulate a total
  let total = 0;
  digits.forEach((digit, index) => {
    const weighingFactor = baseWeighingFactor - index;
    const result = digit * weighingFactor;

    total += result;
  });

  // Divide the total by 11 and establish the remainder
  // The check digit is validated using the Modulus 11 algorithm, excuse the magic number
  const remainder = total % 11;

  // Subtract the remainder from 11 to give the check digit to verify against
  let calculatedCheckDigit = 11 - remainder;
  // If the result is 11 then a check digit of 0 is used
  if (calculatedCheckDigit === 11) {
    calculatedCheckDigit = 0;
  }

  // Check the remainder matches the check digit. If it does not, the NHS number is invalid.
  return calculatedCheckDigit !== checkDigit;
};

export const testInvalidEmail = (value: string) => {
  return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

export const validateNextDueDate = (minDate: Date) =>
  cond<string, string>([
    [testMinLength(1), always("This doesn’t seem right, please try again")],

    [testIncompleteDate, always("Please use the date format dd/mm/yyyy")],

    [testInvalidDate, always("This doesn’t seem right, please try again")],

    [testMinDate(minDate), always("You can’t enter a date in the past")],

    // default: all good, no errors to display
    [T, always("")]
  ]);

export const validateDateOfBirth = cond<string, string>([
  [testMinLength(1), always("Please enter your date of birth.")],

  [testIncompleteDate, always("Please use the date format dd/mm/yyyy.")],

  [testInvalidDate, always("Please enter a valid date of birth.")],

  [testMinDate(yearsAgo(115)), always("Please enter a valid date of birth.")],

  [testMaxDate(yearsAgo(16)), always("You must be over 16 years old.")],

  // default: all good, no errors to display
  [T, always("")]
]);

export const validatePhoneNumber = cond<string, string>([
  // TODO: Add real validation, pending requirements 👀
  [testMinLength(8), always("Please enter a valid phone number")],

  // default: all good, no errors to display
  [T, always("")]
]);

export const validateNhsNumber = cond<string, string>([
  [
    value => {
      const trimmed = value.replace(/\s+/g, "");
      return trimmed.length !== 10;
    },
    always("Please enter your NHS number.")
  ],

  [testInvalidNhsNumber, always("Please enter a valid NHS number.")],

  // default: all good, no errors to display
  [T, always("")]
]);

export const validateEmail = cond<string, string>([
  [value => !value, always("Please enter your email address.")],

  [
    testInvalidEmail,
    always(
      "Please enter your email address in the correct format, like name@example.com"
    )
  ],

  // default: all good, no errors to display
  [T, always("")]
]);

export interface Suggestion {
  address: string;
  domain: string;
  full: string;
}

/**
 * Based on our most common domain names, to get these again query:
 *
 * select
 * substr(email,0,strpos(email, '.')) as beginning,
 * count(distinct accountid) as count,
 * round((count(distinct accountid) / sum(count(distinct accountid)) over()) * 100,1) as ratio
 * from echo.notifications_snap
 * where createtime >= 'YYYY-MM-DD'
 * and email is not null
 * group by 1
 * order by 2 desc
 */
const secondLevelDomains = [
  "gmail",
  "googlemail",
  "hotmail",
  "yahoo",
  "btinternet",
  "outlook",
  "icloud",
  "me",
  "aol",
  "live",
  "sky",
  "ntlworld",
  "talktalk",
  "msn",
  "blueyonder",
  "virginmedia",
  "mail",
  "lloydspharmacy",
  "tiscali",
  "btopenworld",
  "rocketmail",
  "gmx",
  "mac",
  "protonmail"
];

/**
 * To get these again query:
 *
 * select
 * substr(email,strpos(email, '.'), 100) as ending,
 * count(distinct accountid) as count,
 * round((count(distinct accountid) / sum(count(distinct accountid)) over()) * 100,1) as ratio
 * from echo.notifications_snap
 * where createtime >= 'YYYY-MM-DD'
 * and email is not null
 * group by 1
 * order by 2 desc
 */
const topLevelDomains = [
  "co",
  "com",
  "com.au",
  "com.sg",
  "co.jp",
  "co.uk",
  "co.in",
  "co.il",
  "net",
  "net.au",
  "uk",
  "uk.com",
  "org",
  "org.uk",
  "ac.uk",
  "me.uk",
  "gov.uk",
  "nhs.uk",
  "pl",
  "de",
  "fr",
  "it",
  "ru",
  "edu",
  "gov",
  "jp",
  "nl",
  "kr",
  "se",
  "eu",
  "ie",
  "us",
  "at",
  "be",
  "bg",
  "me",
  "dk",
  "hk",
  "es",
  "gr",
  "ch",
  "no",
  "cz",
  "in",
  "info",
  "biz",
  "sg",
  "hu"
];

export const warnEmail = (value: string): Suggestion | undefined => {
  return Mailcheck.run({
    email: value,
    secondLevelDomains,
    topLevelDomains
  });
};
